import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PlayIcon, RefreshCwIcon } from "lucide-react";
import { useEffect } from "react";
import { Button as AriaButton, Heading } from "react-aria-components";
import z from "zod";

import { Alert } from "@/components/base/alert";
import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { useTRPC } from "@/integrations/trpc/react";

export const AUTOPROMOTE_WARNING = {
  title: "Heads up",
  description:
    "Once seeded, auto-promotion is disabled. If a team drops and there is a waitlist, you will need to manually promote waitlisted teams.",
} as const;

const seedDivisionSchema = z.object({
  overwrite: z.boolean(),
});

export type SeedDivisionModalProps = {
  tournamentId: number;
  tournamentDivisionId: number;
  divisionName: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  showAutopromoteWarning: boolean;
};

export function SeedDivisionModal({
  tournamentId,
  tournamentDivisionId,
  divisionName,
  isOpen,
  onOpenChange,
  showAutopromoteWarning,
}: SeedDivisionModalProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, failureReason, reset } = useMutation({
    ...trpc.tournaments.calculateSeeds.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());

      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const form = useAppForm({
    defaultValues: {
      overwrite: false,
    },
    validators: {
      onChange: seedDivisionSchema,
    },
    onSubmit: ({ value: { overwrite } }) => {
      mutate({
        id: tournamentId,
        overwrite,
        tournamentDivisionIds: [tournamentDivisionId],
      });
    },
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Seed Division
        </Heading>

        <p className="mb-6 text-sm text-gray-700">
          Calculate seeds for {divisionName}. If you want to reset the seeds
          entirely, select{" "}
          <span className="font-semibold italic">Overwrite existing</span>.
        </p>

        {showAutopromoteWarning && (
          <Alert color="warning" {...AUTOPROMOTE_WARNING} />
        )}

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to seed division"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppField
            name="overwrite"
            children={(field) => (
              <field.Checkbox label="Overwrite existing" field={field} />
            )}
          />

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton requireChange={false}>Seed</form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}

export function SeedDivisionColumnHeader({
  hasSeed,
  onPress,
}: {
  hasSeed: boolean;
  onPress: () => void;
}) {
  const Icon = hasSeed ? RefreshCwIcon : PlayIcon;

  return (
    <span className="inline-flex items-center gap-1">
      Seed
      <AriaButton
        slot={null}
        aria-label={hasSeed ? "Recalculate seeds" : "Calculate seeds"}
        className="cursor-pointer rounded p-0.5 text-white hover:bg-white/20"
        onPress={onPress}
      >
        <Icon className="size-3.5" />
      </AriaButton>
    </span>
  );
}
